import { Toggle } from '../../../molecules/toggle';
import { useDisableDocumentProcessingMutation, useEnableDocumentProcessingMutation } from '../../../api/documents';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';
import { useAppSelector } from '../../../reducers';
import { getRootConfig } from '../../../selectors/globalData';
import { DataProcessingServiceType, DocumentItem } from '@g17eco/types/document';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { generateErrorToast, generateToast } from '../../../molecules/toasts';

const isSupportedDocumentType = (service: DataProcessingServiceType, extension: string) => {
  const pdfOnly = [DataProcessingServiceType.PrivateAIAgent, DataProcessingServiceType.Experimental];
  if (pdfOnly.includes(service)) {
    return extension === 'pdf';
  }
  const allowedTypes = ['pdf', 'docx', 'doc', 'txt', 'csv', 'xls', 'xlsx', 'rtf', 'xml'];
  return allowedTypes.includes(extension);
};

export const DocumentProcessing = ({ initiativeId, documentItem }: { initiativeId: string, documentItem: DocumentItem }) => {

  const [enableDocumentProcessing, { isLoading: isEnabling }] = useEnableDocumentProcessingMutation();
  const [disableDocumentProcessing, { isLoading: isDisabling }] = useDisableDocumentProcessingMutation();
  const [toggling, setToggling] = useState<{ processingService: DataProcessingServiceType, enable: boolean } | null>(null);
  const isToggling = isEnabling || isDisabling;

  const rootConfig = useAppSelector(getRootConfig);
  const canAccessAIDocumentLibrary = FeaturePermissions.canAccessAIDocumentLibrary(rootConfig);

  const handleToggleProcessing = useCallback((documentId: string, processingService: DataProcessingServiceType, enable: boolean) => {
    setToggling({ processingService, enable });
    if (enable) {
      enableDocumentProcessing({ initiativeId, documentId, type: processingService }).unwrap().then(() => {
        generateToast({
          icon: 'fal fa-check-circle',
          message: 'Document processing has been enabled',
          color: 'success',
        });
      }).catch(generateErrorToast)
      .finally(() => {
        setToggling(null);
      });
    } else {
      disableDocumentProcessing({ initiativeId, documentId, type: processingService }).unwrap().then(() => {
        generateToast({
          icon: 'fal fa-check-circle',
          message: 'Document processing has been disabled',
          color: 'success',
        });
      }).catch(generateErrorToast)
      .finally(() => {
        setToggling(null);
      });
    }
  }, [enableDocumentProcessing, disableDocumentProcessing, initiativeId]);

  const getInfo = useCallback((processingService: DataProcessingServiceType, extension?: string) => {
    const isSupported = isSupportedDocumentType(processingService, extension ?? '');
    const processing = documentItem.processingServices?.find((p) => p.type === processingService);
    const isFailed = Boolean(processing?.failedDate);
    const isDeleted = Boolean(processing?.deletedDate);
    const isCompleted = Boolean(processing?.completedDate && !isFailed && !isDeleted);
    const isProcessing = Boolean(processing?.startedDate && !isCompleted && !isFailed && !isDeleted);
    const canToggle = canAccessAIDocumentLibrary && [DataProcessingServiceType.OpenAIAgent, DataProcessingServiceType.Experimental].includes(processingService);
    return {
      isFailed,
      isCompleted,
      isProcessing,
      canToggle,
      isSupported,
    }
  }, [documentItem.processingServices, canAccessAIDocumentLibrary]);

  const documentExtension = documentItem.metadata?.extension;
  const rows = useMemo(() => {
      return [
        {
          name: 'Open AI Agent',
          processingService: DataProcessingServiceType.OpenAIAgent,
          description: 'Open AI Agent is a tool that allows you to process documents with AI. Full documents will be sent to OpenAI.',
          info: getInfo(DataProcessingServiceType.OpenAIAgent, documentExtension),
        },
        {name: 'Experimental',
          processingService: DataProcessingServiceType.Experimental,
          description: 'Experimental is a tool that allows you to process documents with AI. Only some data is send to public AIs. This process can take about 2-3 minutes per page, so please be patient.',
          info: getInfo(DataProcessingServiceType.Experimental, documentExtension),
        },
        {
          name: 'Private AI Agent',
          processingService: DataProcessingServiceType.PrivateAIAgent,
          description: 'The Private AI Agent is a tool that allows you to process documents with using a privately hosted AI model on Snowflake. This is faster than Experimental, and no data is sent to public AI models, but could be less accurate.',
          info: getInfo(DataProcessingServiceType.PrivateAIAgent, documentExtension),
        },
      ]
    }, [getInfo, documentExtension]);

  return (
    <div className='d-flex flex-column'>
      <table>
        <tbody>
          {rows.map((row) => (
            <Fragment key={row.processingService}>
              <tr>
                <td className={`pt-3 p-1 text-lg ${!row.info.isSupported ? 'text-ThemeTextLight' : ''}`}>{row.name}</td>
                <td className='p-1'>
                  {isToggling && toggling?.processingService === row.processingService ? <i className='fal text-lg fa-spinner fa-spin' /> : (
                    <>
                      {row.info.isFailed ? <i className='fal text-lg fa-exclamation-triangle text-danger' /> : null}
                      {row.info.isCompleted ? <i className='fal text-lg fa-check-circle text-success' /> : null}
                      {row.info.isProcessing ? <i className='fal text-lg fa-spinner fa-spin' /> : null}
                    </>
                  )}
                </td>
                <td className='p-1 text-right'>
                  <Toggle
                    checked={row.info.isCompleted || row.info.isProcessing}
                    disabled={!row.info.isSupported || row.info.isProcessing || Boolean(toggling?.processingService === row.processingService)}
                    onChange={() => handleToggleProcessing(documentItem._id, row.processingService, !row.info.isCompleted)}
                  />
                </td>
              </tr>
              <tr className='border-bottom'>
                <td colSpan={3} className='px-1 pb-3'>{row.info.isSupported ? row.description : <span className='text-danger'>Only PDFs are supported for this processing type</span>}</td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
